import React, { useState, useEffect } from 'react';
import { Signal } from '@catalyst-iq/shared';
import { SignalsTable } from '../components/signalsTable';
import { api } from '../services/api';

const normalizeSignal = (sig: any): any => ({
  ...sig,
  change_pct: sig.change_pct ?? sig.changePct ?? 0,
  rel_volume: sig.rel_volume ?? sig.relVolume ?? 0,
});

interface TopLosersSectionProps {
  onCountChange?: (count: number) => void;
  /** Page-level scan — lights this table even when its own fetch isn't running */
  pageScanning?: boolean;
  scanPulse?: number;
  onScanningChange?: (scanning: boolean) => void;
}

const SCAN_INTERVAL_MS = 8000;
const SCAN_HOLD_MS = 1200;

export const TopLosersSection: React.FC<TopLosersSectionProps> = ({
  onCountChange,
  pageScanning = false,
  scanPulse = 0,
  onScanningChange,
}) => {
  const [losers, setLosers] = useState<Signal[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isScanning, setIsScanning] = useState<boolean>(false);
  const DROP_THRESHOLD = -5.0;

  useEffect(() => {
    onScanningChange?.(isScanning);
  }, [isScanning, onScanningChange]);

  useEffect(() => {
    let isFirstFetch = true;
    let scanHoldId: ReturnType<typeof setTimeout>;
    let isMounted = true;

    const fetchSignals = async () => {
      if (isFirstFetch) setIsLoading(true);
      const scanStartedAt = Date.now();
      setIsScanning(true);
      try {
        const data = await api.getSignals({ sort: 'change_pct_asc', limit: 500 });
        if (!isMounted) return;
        const rawSignals = data.signals || [];

        const processedSignals = rawSignals
          .map(normalizeSignal)
          .filter((sig: any) => sig.change_pct <= DROP_THRESHOLD)
          .map((sig: any) => {
            if (sig.label === 'rebound') {
              return { ...sig, label: 'rebound_green' };
            }
            return { ...sig, label: 'none' };
          })
          .sort((a: any, b: any) => a.change_pct - b.change_pct);

        setLosers(processedSignals);

        if (onCountChange) {
          onCountChange(processedSignals.length);
        }
      } catch (error) {
        console.error('Error fetching signals:', error);
        if (isFirstFetch && isMounted) setLosers([]);
      } finally {
        if (!isMounted) return;
        setIsLoading(false);
        isFirstFetch = false;
        const holdMs = Math.max(0, SCAN_HOLD_MS - (Date.now() - scanStartedAt));
        clearTimeout(scanHoldId);
        scanHoldId = setTimeout(() => {
          if (isMounted) setIsScanning(false);
        }, holdMs);
      }
    };

    fetchSignals();
    const intervalId = setInterval(fetchSignals, SCAN_INTERVAL_MS);
    return () => {
      isMounted = false;
      clearInterval(intervalId);
      clearTimeout(scanHoldId);
    };
    // onCountChange is intentionally omitted — parent passes an inline callback
  }, [DROP_THRESHOLD]);

  const losersIcon = (
    <svg className="w-3.5 h-3.5 text-red-500 dark:text-[#f5476a]" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M10 3a.75.75 0 01.75.75v10.638l3.96-4.158a.75.75 0 111.08 1.04l-5.25 5.5a.75.75 0 01-1.08 0l-5.25-5.5a.75.75 0 111.08-1.04l3.96 4.158V3.75A.75.75 0 0110 3z" clipRule="evenodd"/>
    </svg>
  );

  return (
    <SignalsTable
      title="Top Losers"
      subtitle={`Tickers down ≥ ${Math.abs(DROP_THRESHOLD)}% · ${losers.length} active signals`}
      iconNode={losersIcon}
      topStripGradient="from-[#f5476a] via-[#f97316] to-[#f5a623]"
      headerBgClass="bg-gradient-to-r from-red-50/60 via-orange-50/30 to-white dark:from-[#f5476a]/5 dark:via-[#0f1218] dark:to-[#080c14]"
      emptyMessage="No major decliners right now"
      signals={losers}
      isLoading={isLoading}
      isScanning={isScanning || pageScanning}
      scanPulse={scanPulse}
      reboundTooltip="Drop of {N}% with no negative news in 24h. Not a recommendation."
    />
  );
};
